import React, { useState } from "react"
import styled from "styled-components"
import { useDebouncedCallback } from "use-debounce"
import PropTypes from "prop-types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import Input from "../../ui/Input"

const ClearBtn = styled.button`
  border: none;
  background: none;
  color: ${(props) => props.theme.colours.error};
  padding: 0 0.5rem;
`
const Container = styled.div`
  display: flex;
  width: 100%;
  ${Input} {
    flex: auto;
    width: 100%;
  }
  ${ClearBtn} {
    flex: none;
  }
`

/**
 * A general purpose filter by value of the text.
 * For example an id like 'astrosite2', a collection name, etc
 */
const TextFilter = ({
  filterByName,
  filter = {},
  setFilter,
  textEntryDebounceMs = 5000,
  placeholder,
  active,
  transform,
  showClear = true,
  toolTip,
}) => {
  // Track updates in local state for a smooth typing experience.
  const [text, setText] = useState(filter[filterByName])

  // updateParentFilter calls the given `setFilter`
  // based on local state, i.e. text to search for.
  const updateParentFilter = () => {
    filter = {
      ...filter,
      [filterByName]: transform ? transform(text) : text,
    }
    setFilter(filter)
    setText(transform ? transform(text) : text)
  }

  // But only call `setFilter` debounced, so that we don't
  // cause rerenders and API calls with every keystroke.
  const updateParentFilterDebounced = useDebouncedCallback(
    updateParentFilter,
    textEntryDebounceMs
  )

  // Called when the value changes
  const changed = (e) => {
    setText(e.target.value)
    updateParentFilterDebounced()
  }

  const cleared = () => {
    setText("") // Immediate local state
    filter[filterByName] = ""
    setFilter(filter) // Parent filter
  }

  // TODO: On enter, update immediately
  const keyPressed = (e) => {
    if (e.key === "Enter") {
      updateParentFilter()
    }
  }

  return (
    <Container title={toolTip}>
      <Input
        type="text"
        value={text || ""}
        onKeyPress={keyPressed}
        onChange={changed}
        placeholder={placeholder}
        active={active}
      />
      {showClear && text && (
        <ClearBtn onClick={cleared} title="Clear filter">
          <FontAwesomeIcon icon={faTimes} />
        </ClearBtn>
      )}
    </Container>
  )
}

TextFilter.propTypes = {
  filter: PropTypes.object.isRequired,
  filterByName: PropTypes.string.isRequired, // The variable name inside filter to use as the text value
  setFilter: PropTypes.func.isRequired,
  textEntryDebounceMs: PropTypes.number,
  placeholder: PropTypes.string, // The placeholder value for the input box
  toolTip: PropTypes.string, // Short descriptive or help text
  active: PropTypes.bool, // Indication from parent whether this filter is actively applied
  showClear: PropTypes.bool, // Indicates if the clear button should be shown
  transform: PropTypes.func, // any function you want to pass in to transform the text being input by the user (e.g. converting spaces to commas)
}

export default TextFilter
